import { ExtranetOrderTicketGroupDto } from '@pharmac-extranet/extranet-api'
import {
  AlertIcon,
  CheckIcon,
  ExtranetCatalogItemChevron,
  ExtranetContainer,
  ExtranetExpandArrowColumn,
  ExtranetTable,
  ExtranetTableColumn,
  ExtranetTableGroupColumn,
  ExtranetTableRow,
  formatDate,
  formatDateTimeString,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import { PharmacLoadingDots, PharmacTableColumn, PharmacTableRow, PharmacText } from '@pharmac/ui'
import { weekdayShortTranslations } from '../customer-groups/util/date-utils'
import { NoResultsResponse } from '../responses/NoResultsResponse'
import { OrderTicketCustomersTable } from './OrderTicketCustomersTable'

type Props = {
  requestChecked?: boolean
  noSearchResults?: boolean
  groupData: ExtranetOrderTicketGroupDto[] | undefined
  scrollRef?: (node: HTMLElement | null) => void
  isLoading?: boolean
  fetchNextPage?: () => void
}

export const OrderTicketsTable = ({
  requestChecked,
  noSearchResults,
  groupData,
  scrollRef,
  isLoading,
}: Props) => {
  const { minDesktop } = useBreakpoint()
  const { t } = useLanguage()

  const getWeekDays = (dateString: string) => {
    const date = new Date(dateString)
    const day = date.getDay() - 1
    const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

    return String(t(weekdayShortTranslations(weekDays[day]) as any)).toLocaleUpperCase()
  }

  return (
    <>
      <ExtranetTable>
        {minDesktop && (
          <ExtranetTableRow
            style={{
              backgroundColor: 'var(--color-accent-4)',
              border: 'none',
              fontWeight: 600,
            }}
          >
            <ExtranetTableColumn marginSmallLeft>
              {t('extranet:orderHistory.customerGroup')}
            </ExtranetTableColumn>
            <ExtranetTableColumn>
              {t('extranet:customerGroupSettings.orderTime')}
            </ExtranetTableColumn>
            <ExtranetTableColumn>{t('common:quantity')}</ExtranetTableColumn>
            {!requestChecked && (
              <ExtranetTableColumn>
                {t('extranet:correctionRequests.requestArrivedAt')}
              </ExtranetTableColumn>
            )}
            <ExtranetExpandArrowColumn />
          </ExtranetTableRow>
        )}
        {isLoading && requestChecked && (
          <ExtranetContainer
            style={{ width: '100%' }}
            alignCenter
            justifyCenter
            paddingLarge
          >
            <PharmacLoadingDots />
          </ExtranetContainer>
        )}
        {groupData?.map((a: any) => (
          <ExtranetTableRow
            key={a.customerGroupId}
            expand={
              <OrderTicketCustomersTable
                ticket={a}
                requestChecked={requestChecked}
              />
            }
            toggleExpand={() => null}
            children={({ isExpanded }) => (
              <ExtranetTableGroupColumn>
                <PharmacTableRow>
                  <PharmacTableColumn size={minDesktop ? undefined : 3}>
                    <ExtranetContainer marginSmallLeft>
                      <PharmacText weight="semibold">{a.customerGroupName}</PharmacText>
                    </ExtranetContainer>
                  </PharmacTableColumn>
                  {minDesktop && (
                    <PharmacTableColumn>
                      <PharmacText>
                        {a.deadlineDate ? (
                          <>
                            {getWeekDays(a.deadlineDate)} {formatDate(a.deadlineDate)}{' '}
                            {a.deadlineTime}
                          </>
                        ) : (
                          <>{t('extranet:orderHistory.urgent')}</>
                        )}
                      </PharmacText>
                    </PharmacTableColumn>
                  )}
                  <PharmacTableColumn size={minDesktop ? undefined : 1}>
                    <ExtranetContainer alignCenter>
                      {requestChecked ? (
                        <>
                          <CheckIcon icon="checkCircle" />
                          <PharmacText>{a.ticketCount}</PharmacText>
                        </>
                      ) : (
                        <>
                          <AlertIcon icon="alertTriangle" />
                          <PharmacText>{a.ticketCount}</PharmacText>
                        </>
                      )}
                    </ExtranetContainer>
                  </PharmacTableColumn>
                  {minDesktop && !requestChecked && (
                    <PharmacTableColumn>
                      <PharmacText>{formatDateTimeString(a.latestTicketSentAt)}</PharmacText>
                    </PharmacTableColumn>
                  )}
                  <ExtranetExpandArrowColumn>
                    <ExtranetCatalogItemChevron isExpanded={isExpanded} />
                  </ExtranetExpandArrowColumn>
                </PharmacTableRow>
                {!minDesktop && (
                  <ExtranetTableRow
                    noMargin
                    noBorders
                    style={{
                      backgroundColor: 'var(--color-accent-2)',
                    }}
                  >
                    <PharmacTableColumn>
                      <>
                        <ExtranetContainer
                          flexRow
                          paddingSmall
                        >
                          <ExtranetContainer paddingSmallRight>
                            <PharmacText weight="semibold">
                              {t('extranet:customerGroupSettings.orderTime')}:
                            </PharmacText>
                          </ExtranetContainer>
                          <ExtranetContainer>
                            <PharmacText weight="semibold">
                              {a.deadlineDate ? (
                                <>
                                  {getWeekDays(a.deadlineDate)} {formatDate(a.deadlineDate)}{' '}
                                  {a.deadlineTime}
                                </>
                              ) : (
                                <>{t('extranet:orderHistory.urgent')}</>
                              )}
                            </PharmacText>
                          </ExtranetContainer>
                        </ExtranetContainer>
                      </>
                    </PharmacTableColumn>
                  </ExtranetTableRow>
                )}
                {!minDesktop && !requestChecked && (
                  <ExtranetTableRow
                    noMargin
                    noBorders
                    style={{
                      backgroundColor: 'var(--color-accent-1)',
                      borderBottom:
                        !minDesktop && isExpanded ? '2px solid var(--color-accent-4)' : undefined,
                    }}
                  >
                    <PharmacTableColumn>
                      <>
                        <ExtranetContainer
                          flexRow
                          paddingSmall
                        >
                          <ExtranetContainer paddingSmallRight>
                            <PharmacText>
                              {t('extranet:correctionRequests.requestArrivedAt')}:
                            </PharmacText>
                          </ExtranetContainer>
                          <ExtranetContainer>
                            <PharmacText>{formatDateTimeString(a.latestTicketSentAt)}</PharmacText>
                          </ExtranetContainer>
                        </ExtranetContainer>
                      </>
                    </PharmacTableColumn>
                  </ExtranetTableRow>
                )}
              </ExtranetTableGroupColumn>
            )}
          />
        ))}
        {requestChecked && <div ref={scrollRef} />}
      </ExtranetTable>
      {groupData?.length === 0 && !noSearchResults && (
        <NoResultsResponse
          title={t('extranet:correctionRequests.noCorrectionRequests')}
          icon="smile"
          description={t('extranet:productShortages.allGood')}
        />
      )}
      {groupData?.length === 0 && noSearchResults && <NoResultsResponse />}
    </>
  )
}
